import type { RequestConfig, RequestOptions } from '@@/plugin-request/request';
import { message } from 'antd';
import { history } from 'umi';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: string; // 修改为string类型，与后端保持一致
  errorMessage?: string;
  showType?: ErrorShowType;
  code?: string; // 增加code字段，可能是后端返回的另一个错误编码字段
  message?: string; // 增加message字段，可能是后端返回的另一个错误消息字段
}

const redirect401BizCodes = ['1100002'];

const clearBrowserCookies = () => {
  if (typeof document === 'undefined') return;
  const raw = document.cookie || '';
  if (!raw) return;
  const cookieNames = raw
    .split(';')
    .map((c) => c.trim())
    .filter(Boolean)
    .map((c) => c.split('=')[0])
    .filter(Boolean);
  const paths = new Set<string>(['/']);
  if (typeof location !== 'undefined') {
    const pathname = location.pathname || '/';
    const segments = pathname.split('/').filter(Boolean);
    let acc = '';
    for (const seg of segments) {
      acc += `/${seg}`;
      paths.add(acc);
    }
  }
  for (const name of cookieNames) {
    for (const path of paths) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
    }
  }
};

const redirectTo401IfNeeded = (bizCode: unknown, responseMessage?: string) => {
  if (responseMessage) message.error(responseMessage || '');
  if (!bizCode) return;
  if (redirect401BizCodes.indexOf(String(bizCode)) === -1) return;
  if ((history as any).location?.pathname === '/401') return;
  clearBrowserCookies();
  history.push('/401');
};

// System error code mapping table, based on SysEnum.java
/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res: any) => {
      console.log('errorThrower res:', res); // 打印到控制台
      const {
        success,
        data,
        errorCode,
        errorMessage,
        showType,
        code,
        message: responseMessage,
      } = res as unknown as ResponseStructure;
      if (!success) {
        const normalizedCode = errorCode || code;
        // {
        //     "requestId": "6e13f0ca-eb27-48d3-8201-0b21b27e1d21",
        //     "code": "1100000",
        //     "message": "Token已过期，请重新登录",
        //     "success": false,
        //     "data": null
        // }
        if (normalizedCode === '1100000') {
          // 当前就在登录相关页面时不能再把 pathname 当 redirect，
          // 否则会跳到 /login?redirect=/login 形成死循环
          // 此时 redirect 置空，让 Login 页直接 fallback 到 /home
          const currentPath = location.pathname || '';
          console.log('currentPath:', currentPath);
          const isOnAuthPage =
            currentPath === '/login' ||  
            currentPath === '/user/login' ||
            currentPath === '/401' ||
            currentPath === '/404' ||
            currentPath.startsWith('/login/') ||
            currentPath.startsWith('/user/login/');
          // knowledge 是业务路径，原样保留
          let redirect = isOnAuthPage ? '' : (currentPath || '/');
          history.push({
            pathname: '/login',
            search: redirect ? `?redirect=${encodeURIComponent(redirect)}` : '',
          });
        }

        const normalizedMessage = errorMessage || responseMessage;
        // redirectTo401IfNeeded(normalizedCode, normalizedMessage);
        const error: any = new Error(normalizedMessage);
        error.name = 'BizError';
        error.info = { errorCode: normalizedCode, errorMessage: normalizedMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      console.log('errorHandler error:', error); // 打印到控制台
      if (error?.name === 'BizError') {
        const bizCode = String(error?.info?.errorCode ?? error?.info?.code ?? '');
        if (redirect401BizCodes.indexOf(String(bizCode)) !== -1) {
          redirectTo401IfNeeded(bizCode, error?.info?.errorMessage);
          return;
        }
      }

      if (opts?.skipErrorHandler) throw error;

      if (error.name === 'ValidateError') {
        message.error(error?.message || 'Input cannot contain only spaces');
        return;
      }

      // Handle response timeout
      if (error.code === 'ECONNABORTED') {
        // message.error('Request timeout, please try again later');
        console.error('Request timeout:', error); // 打印到控制台
        return;
      }

      // 我们的 errorThrower 抛出的错误
      console.log('errorHandler BizError:', error); // 打印到控制台
      if (error.name === 'BizError') {
        if (error?.info?.errorMessage) {
          message.error(error.info.errorMessage);
          return;
        }
      } else if (error.response) {
        // Axios 的错误 - 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const { status, data } = error.response;

        // 处理HTTP状态码
        switch (status) {
          case 401:
            // Unauthorized, redirect to login page
            message.error('Unauthorized, please login again');
            history.push('/login');
            break;
          case 403:
            // Forbidden
            message.error('No permission to access this resource');
            break;
          case 404:
            // Resource not found
            message.error('The requested resource does not exist');
            break;
          case 500:
          case 502:
          case 503:
          case 504:
            // Server errors
            message.error('Server internal error, please try again later');
            break;
          default:
            // Other HTTP errors, try to get error info from response data
            if (data) {
              // handleBusinessError(data);
            } else {
              message.error(`Request failed: ${status}`);
            }
        }
      } else if (error.request) {
        // Request was made but no response was received
        message.error('Network connection failed, please check your network settings');
      } else {
        // Something happened in setting up the request
        message.error('Request error, please try again later');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      const url = config?.url?.concat('') || '';
      const method = String((config as any)?.method || '').toUpperCase();
      const isCreate = method === 'POST' && /\/(add|create|copy)/i.test(url);
      const trimAndCheck = (input: any): { value: any; onlyWhitespace: boolean } => {
        if (typeof input === 'string') {
          const trimmed = input.trim();
          return { value: trimmed, onlyWhitespace: input.length > 0 && trimmed.length === 0 };
        }
        if (Array.isArray(input)) {
          let flag = false;
          const next = input.map((it) => {
            const r = trimAndCheck(it);
            flag = flag || r.onlyWhitespace;
            return r.value;
          });
          return { value: next, onlyWhitespace: flag };
        }
        if (input && typeof input === 'object') {
          let flag = false;
          const next: any = {};
          Object.keys(input).forEach((k) => {
            const r = trimAndCheck((input as any)[k]);
            next[k] = r.value;
            flag = flag || r.onlyWhitespace;
          });
          return { value: next, onlyWhitespace: flag };
        }
        return { value: input, onlyWhitespace: false };
      };
      if (isCreate && (config as any).data) {
        const { value, onlyWhitespace } = trimAndCheck((config as any).data);
        if (onlyWhitespace) {
          const err: any = new Error('Some fields contain only spaces. Please correct and submit.');
          err.name = 'ValidateError';
          throw err;
        }
        (config as any).data = value;
      }
      return config;
    },
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理
      const url = config?.url?.concat('');
      // 添加超时配置
      config.timeout = config.timeout || 30000; // 默认30秒超时
      return { ...config, url };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response: any) => {
      console.log('response', response);
      try {
        const currentPath = (history as any).location?.pathname || '';
        // 拦截响应数据，进行个性化处理
        // 首先检查响应内容类型是否为HTML
        const contentType =
          response.headers?.['content-type'] || response.headers?.['Content-Type'];

        if (contentType && contentType.includes('text/html')) {
          // 如果是HTML响应，记录警告并返回原始响应，避免解析错误
          console.warn('Received HTML response instead of JSON:', response.config?.url);
          // 显示用户友好的错误消息
          message.error('System error, please contact administrator');
          return response;
        }

        // 正常处理JSON响应 - 确保数据是对象类型才尝试访问
        if (response.data && typeof response.data === 'object') {
          const data = response.data as unknown as ResponseStructure;
          if (data?.success === false) {
            const bizCode: any = (data as any)?.errorCode ?? (data as any)?.code;
            if (redirect401BizCodes.indexOf(String(bizCode)) !== -1 && currentPath !== '/401') {
              redirectTo401IfNeeded(String(bizCode));
              return response;
            }
            // Response has been intercepted, error handling is unified by errorHandler
            console.warn('Request failed:', data);
            // 移除这里的message.error调用，避免与errorHandler重复显示
          }
        }
        return response;
      } catch (error) {
        // 捕获解析错误，防止应用崩溃
        console.error('Error processing response:', error);
        message.error('Failed to process server response');
        return response;
      }
    },
  ],
};
